import { Link } from 'react-router';
import { SyncBadge } from '../Layout';
import { IconBack, IconChevronDown, IconPin, IconReorder } from './icons';
import { plural } from './status';

interface Props {
  title: string;
  storeName: string | null;
  done: number;
  total: number;
  onPickStore: () => void;
  /** Brak = przełącznik przestawiania ukryty (lista bez sklepu albo zakończona). */
  onToggleReorder?: () => void;
  reordering: boolean;
  /** Połączenie na żywo z domownikami (kropka przy wskaźniku synchronizacji). */
  live: boolean;
  /** Pasek obecności: „Magda też robi te zakupy · 5 odhaczonych”. */
  presence?: string | null;
  /** Propozycje zamienników czekające na decyzję — tap przewija do pierwszej. */
  suggestions?: { count: number; onShow: () => void } | null;
  /** Szacunek listy z cen z paragonów, np. „≈ 240 zł”. */
  estimate?: { text: string; title: string } | null;
}

/** Zwarty nagłówek trybu sklepu: wstecz, tytuł, sklep (zmiana), postęp. */
export function ShopHeader({ title, storeName, done, total, onPickStore, onToggleReorder, reordering, live, presence = null, suggestions = null, estimate = null }: Props) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <header className="safe-top sticky top-0 z-20 border-b border-stone-200 bg-stone-50/95 backdrop-blur dark:border-stone-800 dark:bg-stone-950/95">
      <div className="flex min-h-12 items-center gap-1 pr-2 pl-1">
        <Link
          to="/"
          aria-label="Wróć do list"
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-stone-600 active:bg-stone-200 dark:text-stone-300 dark:active:bg-stone-800"
        >
          <IconBack />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="truncate leading-tight font-semibold">{title}</h1>
          <div className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              onClick={onPickStore}
              className="-mx-1 flex min-w-0 items-center gap-1 rounded px-1 text-sm text-stone-600 active:bg-stone-200 dark:text-stone-400 dark:active:bg-stone-800"
            >
              <IconPin className="size-3.5 shrink-0" />
              <span className="truncate">{storeName ?? 'Wybierz sklep'}</span>
              <IconChevronDown className="size-3.5 shrink-0" />
            </button>
            {estimate && (
              <span title={estimate.title} className="shrink-0 text-sm whitespace-nowrap text-stone-500 tabular-nums">
                · {estimate.text}
              </span>
            )}
          </div>
        </div>
        <SyncBadge />
        <span
          role="img"
          aria-label={live ? 'Na żywo z domownikami' : 'Bez połączenia na żywo'}
          title={live ? 'Na żywo' : 'Bez połączenia na żywo'}
          className={`mx-1 size-2 shrink-0 rounded-full ${live ? 'bg-brand-500' : 'bg-stone-300 dark:bg-stone-600'}`}
        />
        {onToggleReorder && (
          <button
            type="button"
            onClick={onToggleReorder}
            aria-pressed={reordering}
            aria-label="Przestaw kolejność"
            className={`flex size-11 shrink-0 items-center justify-center rounded-full ${
              reordering ? 'bg-brand-700 text-white' : 'text-stone-600 active:bg-stone-200 dark:text-stone-300 dark:active:bg-stone-800'
            }`}
          >
            <IconReorder />
          </button>
        )}
        <span className="shrink-0 pl-1 text-sm font-semibold tabular-nums">
          {done} / {total}
        </span>
      </div>
      <div className="h-1 bg-stone-200 dark:bg-stone-800" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label="Postęp zakupów">
        <div className="h-full bg-brand-600 transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
      {presence && (
        <p className="flex items-center gap-2 bg-sky-50 px-4 py-1.5 text-sm text-sky-900 dark:bg-sky-900/30 dark:text-sky-100">
          <span className="size-2 shrink-0 rounded-full bg-sky-500 motion-safe:animate-pulse" aria-hidden />
          <span className="line-clamp-2 min-w-0">{presence}</span>
        </p>
      )}
      {suggestions && (
        <button
          type="button"
          onClick={suggestions.onShow}
          className="flex min-h-11 w-full items-center gap-2 bg-sky-50 px-4 text-left text-sm text-sky-900 active:bg-sky-100 dark:bg-sky-900/30 dark:text-sky-100 dark:active:bg-sky-900/50"
        >
          <span className="rounded-full bg-sky-600 px-2.5 py-0.5 font-semibold text-white">
            {suggestions.count} {plural(suggestions.count, 'propozycja', 'propozycje', 'propozycji')}
          </span>
          <span className="min-w-0 truncate">zamiennika — pokaż</span>
        </button>
      )}
    </header>
  );
}
